import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Trophy, Medal, Users, User, Boxes, ClipboardCheck, Activity, Settings, LogOut, Radio, Shield, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/tornei", label: "Tornei", icon: Trophy },
  { to: "/campionati", label: "Campionati", icon: Medal },
  { to: "/clan", label: "Clan", icon: Shield },
  { to: "/squadre", label: "Squadre", icon: Users },
  { to: "/giocatori", label: "Giocatori", icon: User },
  { to: "/verifiche-kd", label: "Verifiche K/D", icon: ShieldCheck },
  { to: "/lobby", label: "Lobby", icon: Boxes },
  { to: "/risultati", label: "Risultati", icon: ClipboardCheck },
  { to: "/classifiche", label: "Classifiche Live", icon: Radio },
  { to: "/utenti", label: "Utenti", icon: ShieldCheck, admin: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings },
  { to: "/log", label: "Log Attività", icon: Activity, admin: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const viewerPaths = new Set([
    "/clan",
    "/squadre",
    "/giocatori",
    "/lobby",
    "/classifiche",
  ]);

  return (
    <div className="relative z-10 min-h-screen">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#080808] border-b border-[var(--steel)]/40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={LOGO} alt="SEALS" className="w-8 h-8 object-contain" />
          <span className="heading text-xl gold-text">SEALS</span>
        </div>
        <button data-testid="dashboard-menu-btn" onClick={() => setOpen(true)} className="text-[var(--gold)]"><Menu className="w-6 h-6" /></button>
      </div>

      {/* Overlay */}
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside className={`w-64 bg-[#080808] border-r border-[var(--steel)]/40 flex flex-col fixed h-screen top-0 z-50 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="p-5 border-b border-[var(--steel)]/30 flex items-center gap-3">
          <img src={LOGO} alt="SEALS" className="w-12 h-12 object-contain clip-beveled-sm" />
          <div className="flex-1">
            <div className="heading text-2xl gold-text leading-none">SEALS</div>
            <div className="font-rajdhani text-[10px] tracking-[0.3em] text-[var(--silver)]">ALLIES FORCES</div>
          </div>
          <button className="lg:hidden text-[var(--silver)]" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.filter((n) => {
            if (user?.role === "viewer") return viewerPaths.has(n.to);
            return !n.admin || user?.role === "admin";
          }).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={`/dashboard${to === "/" ? "" : to}`}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 font-rajdhani uppercase tracking-wider text-sm transition-colors ${
                  isActive
                    ? "text-[var(--gold-light)] bg-[#1a1a1a] border-l-4 border-[var(--gold)]"
                    : "text-[var(--silver)] hover:text-[var(--gold)] hover:bg-[#141414] border-l-4 border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[var(--steel)]/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[var(--gold)] clip-beveled-sm flex items-center justify-center text-black font-bold font-oswald">A</div>
            <div className="leading-tight">
              <div className="font-rajdhani font-bold text-white text-sm truncate max-w-[140px]">{user?.name || "Admin"}</div>
              <div className="font-rajdhani text-[10px] text-[var(--gold)] uppercase tracking-wider">Organizzatore</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/dashboard/login"); }}
            className="btn-outline-gold clip-beveled-sm w-full py-2 text-xs flex items-center justify-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </div>
      </aside>

      <main className="min-w-0 lg:ml-64 pt-20 lg:pt-6 p-4 sm:p-6 boot-in">{children}</main>
    </div>
  );
}
